import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/rbac";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Trash2, MessageSquare, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  Radiologist: "bg-sky-50 text-sky-700",
  Cardiologist: "bg-violet-50 text-violet-700",
  Technician: "bg-amber-50 text-amber-700",
  Admin: "bg-red-50 text-red-700",
  Surgeon: "bg-teal-50 text-teal-700",
};

function formatDate(iso) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
}

export default function StudyDiscussion({ studyId, studyDescription, compact }) {
  const { user: authUser } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const bottomRef = useRef(null);

  const user = {
    name: authUser?.name ?? "User",
    role: ROLE_LABELS[authUser?.role] ?? authUser?.role?.replace(/_/g, " ") ?? "",
  };

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/studies/${studyId}/comments`);
      setComments(res.data.comments);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [studyId]);

  useEffect(() => {
    if (studyId) { setLoading(true); fetchComments(); }
  }, [studyId, fetchComments]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim()) { toast.error("Comment cannot be empty"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/studies/${studyId}/comments`, {
        author_name: user.name,
        author_role: user.role,
        body: body.trim(),
      });
      setBody("");
      fetchComments();
    } catch (e) {
      toast.error("Failed to post comment");
    }
    setSubmitting(false);
  };

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/studies/${studyId}/comments/${commentId}`);
      fetchComments();
      toast.success("Comment deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="study-discussion">
      {/* Header */}
      {!compact && (
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2 mb-0.5">
            <MessageSquare className="w-4 h-4 text-[var(--xl-primary)]" />
            <h3 className="text-sm font-semibold">Discussion</h3>
            <Badge variant="secondary" className="text-[10px] ml-1">{comments.length}</Badge>
          </div>
          {studyDescription && (
            <p className="text-xs text-muted-foreground truncate">{studyDescription}</p>
          )}
        </div>
      )}

      {/* Comments list */}
      <ScrollArea className="flex-1 px-4 py-3">
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-8">Loading...</p>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-7 h-7 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No comments yet</p>
            <p className="text-xs text-muted-foreground mt-1">Start the discussion below</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((c) => (
              <div key={c.id} className="group" data-testid={`comment-${c.id}`}>
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-[var(--xl-primary)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-3 h-3 text-[var(--xl-primary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-medium">{c.author_name}</span>
                      {c.author_role && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${ROLE_COLORS[c.author_role] || "bg-slate-100 text-slate-600"}`}>
                          {c.author_role}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground">{formatDate(c.created_at)}</span>
                    </div>
                    <p className="text-xs text-foreground/80 leading-relaxed whitespace-pre-wrap">{c.body}</p>
                  </div>
                  <button
                    onClick={() => deleteComment(c.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 transition-opacity flex-shrink-0"
                    data-testid={`delete-comment-${c.id}`}
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </button>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </ScrollArea>

      {/* Comment input */}
      <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-border" data-testid="comment-form">
        <div className="flex gap-2">
          <Textarea
            data-testid="comment-body"
            placeholder="Add a comment..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            className="text-xs resize-none flex-1"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit(e);
            }}
          />
          <Button
            type="submit"
            data-testid="submit-comment"
            disabled={submitting || !body.trim()}
            size="icon"
            className="h-auto w-9 bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)]"
          >
            <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
        <p className="text-[9px] text-muted-foreground mt-1">Posting as {user.name} &middot; Ctrl+Enter to submit</p>
      </form>
    </div>
  );
}
